/**
 * The top-level keys of the context a JavaScript expression runs against
 * (`JSRunner.evaluate`). `__get` resolves a path that starts anywhere else to
 * `undefined` without an error.
 */
export const GETTER_ROOTS = [
  'kinematicState',
  'variables',
  'routine',
  'sensors',
] as const;

/** The path the editor stores for a name it could not resolve. */
export const INVALID_GETTER_PATH = 'INVALID';

export type GetterCall = {
  text: string;
  index: number;
  path: string;
  /** Null for the one-argument form the editor writes inside sensor value transforms. */
  label: string | null;
  dynamicKey: string | null;
};

export type GetterCallScan = {
  calls: GetterCall[];
  /** `__get(` occurrences whose arguments are not one to three string literals. */
  malformed: Array<{ text: string; index: number }>;
};

// Each argument is a single- or double-quoted literal (two capture groups per
// argument), and whitespace is allowed wherever `GETTER_START_RE` allows it:
// a call this regex misses but that regex matches is reported as malformed
// and blocks Play, so runtime-valid spellings must not fall in the gap.
const GETTER_CALL_RE =
  /\b__get\s*\(\s*(?:"([^"]*)"|'([^']*)')\s*(?:,\s*(?:"([^"]*)"|'([^']*)')\s*(?:,\s*(?:"([^"]*)"|'([^']*)')\s*)?)?\)/g;

const GETTER_START_RE = /\b__get\s*\(/g;

export function scanGetterCalls(expression: string): GetterCallScan {
  const calls: GetterCall[] = [];
  const matchedStarts = new Set<number>();

  for (const match of expression.matchAll(GETTER_CALL_RE)) {
    const index = match.index ?? 0;
    matchedStarts.add(index);

    calls.push({
      text: match[0],
      index,
      path: match[1] ?? match[2] ?? '',
      label: match[3] ?? match[4] ?? null,
      dynamicKey: match[5] ?? match[6] ?? null,
    });
  }

  const malformed: GetterCallScan['malformed'] = [];

  for (const match of expression.matchAll(GETTER_START_RE)) {
    const index = match.index ?? 0;
    if (matchedStarts.has(index)) continue;

    const close = expression.indexOf(')', index);

    malformed.push({
      text: expression.slice(index, close === -1 ? undefined : close + 1),
      index,
    });
  }

  return { calls, malformed };
}

/**
 * Rewrite `__get` calls whose first two arguments are spaced differently from
 * what `getJavaScriptExpressionForVariable` emits. The editor only recognizes
 * `__get("<path>","<label>"`, so `__get("a", "b")` would otherwise read as an
 * unknown variable and be saved back as `__get("INVALID", …)`. Calls already in
 * that form are left exactly as written.
 */
export function canonicalizeGetterCalls(expression: string): string {
  return expression.replace(
    GETTER_CALL_RE,
    (
      match: string,
      dqPath?: string,
      sqPath?: string,
      dqLabel?: string,
      sqLabel?: string,
      dqKey?: string,
      sqKey?: string,
    ) => {
      const path = dqPath ?? sqPath ?? '';
      const label = dqLabel ?? sqLabel;
      const dynamicKey = dqKey ?? sqKey;

      if (label == null) return match;
      if (match.startsWith(`__get("${path}","${label}"`)) return match;

      // A single-quoted argument may contain a double quote, which the
      // canonical form cannot hold unescaped; the call still scans fine.
      if ([path, label, dynamicKey].some((part) => part?.includes('"'))) {
        return match;
      }

      return dynamicKey == null
        ? `__get("${path}","${label}")`
        : `__get("${path}","${label}", "${dynamicKey}")`;
    },
  );
}

export function getterPathRoot(path: string): string {
  return path.match(/^[^.[]*/)?.[0] ?? '';
}

export function isKnownGetterRoot(path: string): boolean {
  return (GETTER_ROOTS as readonly string[]).includes(getterPathRoot(path));
}

/** The entity id a `variables['<id>']…` path reads, or null for other paths. */
export function getterVariableID(path: string): string | null {
  return (
    path
      .match(/^variables\[(?:'([^']+)'|"([^"]+)")\]/)
      ?.slice(1)
      .find(Boolean) ?? null
  );
}

/** The field a `variables['<id>'].<field>` path reads, or null for the bare variable. */
export function getterVariableField(path: string): string | null {
  return (
    path.match(/^variables\[(?:'[^']+'|"[^"]+")\]\.([A-Za-z_$][\w$]*)/)?.[1] ??
    null
  );
}

/** The port a `kinematicState.ioState['<port>']` path reads, or null. */
export function getterIOPort(path: string): string | null {
  return (
    path
      .match(/^kinematicState\.ioState\[(?:'([^']+)'|"([^"]+)")\]/)
      ?.slice(1)
      .find(Boolean) ?? null
  );
}

/** The space id a `routine.positionLists['<id>']` path reads, or null. */
export function getterSpaceID(path: string): string | null {
  return (
    path
      .match(/^routine\.positionLists\[(?:'([^']+)'|"([^"]+)")\]/)
      ?.slice(1)
      .find(Boolean) ?? null
  );
}
